import React, { memo } from 'react';
import { NodeProps, NodeResizer } from 'reactflow';

interface SiteGroupData {
    label: string;
    location: { city: string; country: string };
    nodeCount: number;
    pathHighlight?: 'source';
    /** Driven by the parent's `selectedSiteId` state — true when this site
     *  is the user's currently focused container. Mounts the resize
     *  handles and paints the brand-coloured selection ring. */
    isSelected?: boolean;
}

const ServerIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3" y="4" width="18" height="6" rx="1.5" />
        <rect x="3" y="14" width="18" height="6" rx="1.5" />
        <line x1="6.5" y1="7" x2="6.6" y2="7" />
        <line x1="6.5" y1="17" x2="6.6" y2="17" />
    </svg>
);

const SiteGroupNode: React.FC<NodeProps<SiteGroupData>> = ({ data }) => {
    const isPathSource = data.pathHighlight === 'source';
    const isSelected = !!data.isSelected;

    // Border / ring priority:
    //   1. path-source highlight (graph-level state) — green
    //   2. user selection — brand colour, 2px outer ring
    //   3. default — subtle gray
    // We deliberately use a *flat* outer ring (no blur, no drop-shadow,
    // no transition) so the GPU doesn't have to repaint a Gaussian blur
    // on every frame of a drag — that was the source of the lag/jitter
    // we saw in earlier iterations.
    const borderColor = isPathSource
        ? 'var(--color-semantic-text-success)'
        : isSelected
        ? 'var(--color-semantic-fg-brand-primary)'
        : 'var(--color-semantic-border-secondary)';

    const boxShadow = isPathSource
        ? '0 0 0 2px var(--color-semantic-text-success)'
        : isSelected
        ? '0 0 0 2px var(--color-semantic-fg-brand-primary)'
        : 'none';

    return (
        <div
            className="rounded-lg flex flex-col"
            style={{
                width: '100%',
                height: '100%',
                pointerEvents: 'auto',
                background: 'transparent',
                border: `1px solid ${borderColor}`,
                boxShadow,
                // Cursor handled globally via the <style> block in
                // TopologyGraph (`.react-flow__node-siteGroup` /
                // `.dragging` / `.is-selected`). One CSS source of truth.
            }}
        >
            {/* Resize affordance — only when selected.
                NodeResizer renders 8 hit zones (4 edges + 4 corners), each
                its own DOM target with its own mousedown listener and the
                correct directional cursor (n / s / e / w / ne / nw / se /
                sw-resize). Per the spec, "Resize handle" wins the priority
                resolution against the underlying site body — a click on a
                handle starts a resize, not a drag.

                Visual: kept invisible (transparent border + transparent
                squares) so the brand ring above is the single visible cue
                of "this site is interactive". The 6 px line border-width
                gives a forgiving hit zone — the cursor only has to graze
                the visible ring for the resize cursor to engage. */}
            <NodeResizer
                isVisible={isSelected}
                lineStyle={{
                    borderColor: 'transparent',
                    borderWidth: 6,
                    pointerEvents: 'all',
                }}
                handleStyle={{
                    width: 18,
                    height: 18,
                    background: 'transparent',
                    borderColor: 'transparent',
                    pointerEvents: 'all',
                }}
            />

            {/* Top accent bar */}
            <div
                className="w-full shrink-0 rounded-t-lg"
                style={{
                    height: 3,
                    background: isPathSource
                        ? 'var(--color-semantic-text-success)'
                        : 'var(--color-semantic-fg-brand-primary)',
                    opacity: 0.85,
                }}
            />

            {/* Header row — opaque so the site name reads clearly even when
                lines pass through the site group body underneath. */}
            <div
                className="flex items-center gap-2 px-3 py-2 shrink-0
                    bg-semantic-bg-primary"
            >
                <span className="text-semantic-text-secondary shrink-0">
                    <ServerIcon size={13} />
                </span>
                <span
                    className="text-[12px] font-bold tracking-wide
                        text-semantic-text-primary font-mono"
                >
                    {data.label}
                </span>
                <span
                    className="ml-auto text-[10px] font-medium tracking-wider
                        uppercase text-semantic-text-secondary"
                >
                    {data.location.city}, {data.location.country}
                </span>
            </div>
        </div>
    );
};

export default memo(SiteGroupNode);
